import { create } from "zustand";

/**
 * Favorites (owner, 2026-10-03): any page can be a favorite. A module page,
 * one wiki article, one project, a filtered ledger view. The star in the
 * top-right bar adds the page you are on; the sidebar lists them as rows in
 * the panel, in Simple, More, and Files.
 *
 * A favorite is an ADDRESS: pathname + query, which is everything that decides
 * what renders (Rule 32; the hash only scrolls, so it is not part of it). Its
 * label is captured when it is starred, so the sidebar can name the page
 * without loading it. Per device, like the theme and the sidebar profile.
 */
export interface Favorite {
	/** pathname + search, e.g. "/wiki/alvsala" or "/projects?project=boat". */
	path: string;
	label: string;
}

const STORAGE_KEY = "agnt-favorites";

/** The address a favorite is keyed by. */
export function favoritePath(location: { pathname: string; search: string }): string {
	return `${location.pathname}${location.search}`;
}

export function readFavorites(): Favorite[] {
	try {
		const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
		if (!Array.isArray(raw)) return [];
		const seen = new Set<string>();
		const out: Favorite[] = [];
		for (const entry of raw) {
			if (!entry || typeof entry !== "object") continue;
			const { path, label } = entry as Record<string, unknown>;
			if (typeof path !== "string" || !path.startsWith("/") || seen.has(path)) continue;
			seen.add(path);
			out.push({ path, label: typeof label === "string" && label.trim() ? label.trim() : path });
		}
		return out;
	} catch {
		return []; // unavailable or not JSON — no favorites
	}
}

function writeFavorites(favorites: Favorite[]): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
	} catch {
		/* storage unavailable — the change still applies this session */
	}
}

interface FavoritesState {
	favorites: Favorite[];
	/** Star the page, or un-star it when it is already a favorite. */
	toggle: (favorite: Favorite) => void;
	remove: (path: string) => void;
}

export const useFavoritesStore = create<FavoritesState>((set) => ({
	favorites: readFavorites(),
	toggle: (favorite) =>
		set((s) => {
			const favorites = s.favorites.some((f) => f.path === favorite.path)
				? s.favorites.filter((f) => f.path !== favorite.path)
				: [...s.favorites, favorite];
			writeFavorites(favorites);
			return { favorites };
		}),
	remove: (path) =>
		set((s) => {
			const favorites = s.favorites.filter((f) => f.path !== path);
			writeFavorites(favorites);
			return { favorites };
		}),
}));
